import React, { useState } from 'react';
import axios from 'axios';
import { Send, Mail, Building2, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';

export const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    company: '',
    interest: '',
    message: '',
    website: '', // honeypot — must remain empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(
        '/api/contact',
        {
          full_name: formData.fullName,
          work_email: formData.workEmail,
          company: formData.company,
          area_of_interest: formData.interest,
          message: formData.message,
          website: formData.website,
        },
        { timeout: 20000 }
      );
      toast({
        title: 'Message Sent Successfully',
        description: 'We will respond within two working days. A confirmation has been sent to your email.',
      });
      setFormData({
        fullName: '',
        workEmail: '',
        company: '',
        interest: '',
        message: '',
        website: '',
      });
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Could not send your message. Please email info@jixdo.com directly.';
      toast({
        title: 'Submission Failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        {/* Full Name */}
        <div className="space-y-2">
          <label htmlFor="fullName" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#A9BDD1] flex items-center gap-2">
            <User size={12} className="text-[#19B5FF] stroke-[1.5]" />
            Full Name
          </label>
          <Input
            id="fullName"
            type="text"
            placeholder="John Smith"
            value={formData.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            required
            className="bg-[#06121E]/60 border-[rgba(120,170,210,0.18)] text-[#EAF4FF] placeholder:text-[#A9BDD1]/50 focus:border-[#19B5FF] transition-colors h-11 rounded-md"
          />
        </div>

        {/* Work Email */}
        <div className="space-y-2">
          <label htmlFor="workEmail" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#A9BDD1] flex items-center gap-2">
            <Mail size={12} className="text-[#19B5FF] stroke-[1.5]" />
            Work Email
          </label>
          <Input
            id="workEmail"
            type="email"
            placeholder="john.smith@company.com"
            value={formData.workEmail}
            onChange={(e) => handleChange('workEmail', e.target.value)}
            required
            className="bg-[#06121E]/60 border-[rgba(120,170,210,0.18)] text-[#EAF4FF] placeholder:text-[#A9BDD1]/50 focus:border-[#19B5FF] transition-colors h-11 rounded-md"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Company */}
        <div className="space-y-2">
          <label htmlFor="company" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#A9BDD1] flex items-center gap-2">
            <Building2 size={12} className="text-[#19B5FF] stroke-[1.5]" />
            Company
          </label>
          <Input
            id="company"
            type="text"
            placeholder="Your Company Ltd."
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            required
            className="bg-[#06121E]/60 border-[rgba(120,170,210,0.18)] text-[#EAF4FF] placeholder:text-[#A9BDD1]/50 focus:border-[#19B5FF] transition-colors h-11 rounded-md"
          />
        </div>

        {/* Interest */}
        <div className="space-y-2">
          <label htmlFor="interest" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#A9BDD1] flex items-center gap-2">
            <MessageSquare size={12} className="text-[#19B5FF] stroke-[1.5]" />
            Area of Interest
          </label>
          <Select value={formData.interest} onValueChange={(value) => handleChange('interest', value)} required>
            <SelectTrigger className="bg-[#06121E]/60 border-[rgba(120,170,210,0.18)] text-[#EAF4FF] focus:border-[#19B5FF] h-11 rounded-md">
              <SelectValue placeholder="Select an area" />
            </SelectTrigger>
            <SelectContent className="bg-[#10263A] border-[rgba(120,170,210,0.18)] text-[#EAF4FF]">
              <SelectItem value="identity-cloud">Identity, Cloud & Infrastructure</SelectItem>
              <SelectItem value="publishing-accessibility">Publishing, Conversion & Accessibility</SelectItem>
              <SelectItem value="creative-marketing">Creative, Brand & Marketing Operations</SelectItem>
              <SelectItem value="security">Security & Infrastructure Support</SelectItem>
              <SelectItem value="data-conversion">Data Conversion & Digital Formats</SelectItem>
              <SelectItem value="analytics">Marketing Operations & Analytics</SelectItem>
              <SelectItem value="other">Other / Multiple Areas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <label htmlFor="message" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#A9BDD1]">
          Project Scope / Problem / Timing
        </label>
        <Textarea
          id="message"
          placeholder="Tell us about your project requirements, current challenges, and preferred timeline..."
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          required
          rows={5}
          className="bg-[#06121E]/60 border-[rgba(120,170,210,0.18)] text-[#EAF4FF] placeholder:text-[#A9BDD1]/50 focus:border-[#19B5FF] transition-colors resize-none rounded-md"
        />
      </div>

      {/* Honeypot — hidden from real users, bots fill it */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label htmlFor="website">Website (leave blank)</label>
        <input
          type="text"
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
        />
      </div>

      {/* Submit Button */}
      <div className="pt-2">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#19B5FF] hover:bg-[#48C8FF] text-[#06121E] font-semibold py-6 h-auto rounded-md transition-all duration-300 hover:shadow-lg hover:shadow-[#19B5FF]/30 flex items-center justify-center gap-2 text-[15px] tracking-tight"
        >
          {isSubmitting ? (
            'Sending...'
          ) : (
            <>
              Discuss Your Project
              <Send size={16} className="stroke-[1.5]" />
            </>
          )}
        </Button>
        <p className="text-center text-xs text-[#A9BDD1] mt-4 leading-relaxed">
          By submitting, you agree to be contacted by Jixdo. Response within{' '}
          <span className="text-[#19B5FF] font-semibold">two working days</span>.
        </p>
      </div>
    </form>
  );
};